import React, { useState, useRef, useEffect } from "react";
import { X, Upload } from "lucide-react";

interface SignatureAndStampProps {}

const SignatureAndStamp: React.FC<SignatureAndStampProps> = () => {
  const [signature, setSignature] = useState<string | null>(null);
  const [seals, setSeals] = useState<string[]>([
    "https://via.placeholder.com/150", // Mock data
  ]);
  const [stamps, setStamps] = useState<string[]>([
    "https://via.placeholder.com/150", // Mock data
    "https://via.placeholder.com/150",
    "https://via.placeholder.com/150",
  ]);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  // File upload states
  const [sealFile, setSealFile] = useState<File | null>(null);
  const [stampFile, setStampFile] = useState<File | null>(null);

  const sealInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);

  const handleSealUpload = () => {
    if (sealFile) {
      const url = URL.createObjectURL(sealFile);
      setSeals([...seals, url]);
      setSealFile(null);
      if (sealInputRef.current) sealInputRef.current.value = "";
    }
  };

  const handleStampUpload = () => {
    if (stampFile) {
      const url = URL.createObjectURL(stampFile);
      setStamps([...stamps, url]);
      setStampFile(null);
      if (stampInputRef.current) stampInputRef.current.value = "";
    }
  };

  const removeSeal = (index: number) => {
    const newSeals = [...seals];
    newSeals.splice(index, 1);
    setSeals(newSeals);
  };

  const removeStamp = (index: number) => {
    const newStamps = [...stamps];
    newStamps.splice(index, 1);
    setStamps(newStamps);
  };

  return (
    <div className="flex flex-col gap-y-8">
      {/* Signature Section */}
      <div className="grid grid-cols-2 gap-x-8">
        {/* View Area */}
        <div className="border border-[#E9EAEB] rounded-xl h-64 flex items-center justify-center bg-[#F9FAFB] overflow-hidden relative">
          {signature ? (
            <img
              src={signature}
              alt="Signature"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <span className="text-gray-400">No signature</span>
          )}
        </div>

        {/* Action Area */}
        <div className="border border-[#E9EAEB] rounded-xl h-64 flex flex-col items-center justify-center bg-white p-8 text-center">
          <button
            onClick={() => setIsSignatureModalOpen(true)}
            className="px-8 py-1.5 border border-[#00B074] text-[#00B074] rounded-lg font-medium hover:bg-[#E6F7F1] transition-colors mb-4"
          >
            Signature
          </button>
          <p className="text-[#667085] text-sm">
            Click on the button and sign your virtual signature.
          </p>
        </div>
      </div>

      {/* Judiciary Seals Section */}
      <div className="flex flex-col gap-y-4">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">
          Judiciary seals
        </h3>
        <div className="grid grid-cols-2 gap-x-8">
          {/* View Area */}
          <div className="border border-[#E9EAEB] rounded-xl min-h-[160px] p-6 flex flex-wrap gap-4 items-start bg-white">
            {seals.length === 0 && (
              <span className="text-gray-400 m-auto">No seals uploaded</span>
            )}
            {seals.map((seal, index) => (
              <div
                key={index}
                className="relative w-24 h-24 border border-[#E9EAEB] rounded-lg p-2 flex items-center justify-center bg-white"
              >
                <img
                  src={seal}
                  alt={`Seal ${index + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
                <button
                  onClick={() => removeSeal(index)}
                  className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 hover:bg-red-200 transition-colors border border-white shadow-sm"
                >
                  <X className="w-3 h-3 text-red-600" />
                </button>
              </div>
            ))}
          </div>

          {/* Upload Area */}
          <div className="border border-[#E9EAEB] rounded-xl p-8 flex flex-col justify-center bg-white">
            <div className="flex gap-x-2 mb-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  readOnly
                  value={sealFile ? sealFile.name : ""}
                  placeholder="No file chosen"
                  className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] bg-[#F9FAFB] focus:outline-none"
                />
                <input
                  type="file"
                  ref={sealInputRef}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSealFile(e.target.files[0]);
                    }
                  }}
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <button className="absolute right-0 top-0 bottom-0 px-4 bg-[#E4E7EC] text-[#1A1A1A] font-medium rounded-r-lg hover:bg-gray-300 transition-colors border-y border-r border-[#E9EAEB]">
                  Browse
                </button>
              </div>
            </div>
            <p className="text-xs text-[#98A2B3] text-center mb-4">
              You can upload more that one seal here
            </p>
            <button
              onClick={handleSealUpload}
              disabled={!sealFile}
              className={`w-fit px-7 max-w-xs mx-auto py-1.5 rounded-lg font-medium transition-colors ${
                sealFile
                  ? "bg-[#00B074] text-white hover:bg-[#009b65]"
                  : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
              }`}
            >
              Update
            </button>
          </div>
        </div>
      </div>

      {/* Registrar's Stamps Section */}
      <div className="flex flex-col gap-y-4">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">
          Registrar's stamps
        </h3>
        <div className="grid grid-cols-2 gap-x-8">
          {/* View Area */}
          <div className="border border-[#E9EAEB] rounded-xl min-h-[160px] p-6 flex flex-wrap gap-4 items-start bg-white">
            {stamps.length === 0 && (
              <span className="text-gray-400 m-auto">No stamps uploaded</span>
            )}
            {stamps.map((stamp, index) => (
              <div key={index} className="flex flex-col items-center gap-y-2">
                <div className="relative w-24 h-24 border border-[#E9EAEB] rounded-lg p-2 flex items-center justify-center bg-white">
                  <img
                    src={stamp}
                    alt={`Stamp ${index + 1}`}
                    className="max-w-full max-h-full object-contain"
                  />
                  <button
                    onClick={() => removeStamp(index)}
                    className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 hover:bg-red-200 transition-colors border border-white shadow-sm"
                  >
                    <X className="w-3 h-3 text-red-600" />
                  </button>
                </div>
                <span className="text-sm text-[#1A1A1A] font-medium">
                  Stamp {index + 1}
                </span>
              </div>
            ))}
          </div>

          {/* Upload Area */}
          <div className="border border-[#E9EAEB] rounded-xl p-8 flex flex-col justify-center bg-white">
            <div className="flex gap-x-2 mb-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  readOnly
                  value={stampFile ? stampFile.name : ""}
                  placeholder="No file chosen"
                  className="w-full px-4 py-3 border border-[#E9EAEB] rounded-lg text-[#667085] bg-[#F9FAFB] focus:outline-none"
                />
                <input
                  type="file"
                  ref={stampInputRef}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setStampFile(e.target.files[0]);
                    }
                  }}
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <button className="absolute right-0 top-0 bottom-0 px-4 bg-[#E4E7EC] text-[#1A1A1A] font-medium rounded-r-lg hover:bg-gray-300 transition-colors border-y border-r border-[#E9EAEB]">
                  Browse
                </button>
              </div>
            </div>
            <p className="text-xs text-[#98A2B3] text-center mb-4">
              You can upload more that one stamp here
            </p>
            <button
              onClick={handleStampUpload}
              disabled={!stampFile}
              className={`w-fit px-7 max-w-xs mx-auto py-1.5 rounded-lg font-medium transition-colors ${
                stampFile
                  ? "bg-[#00B074] text-white hover:bg-[#009b65]"
                  : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
              }`}
            >
              Update
            </button>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {isSignatureModalOpen && (
        <SignatureModal
          onClose={() => setIsSignatureModalOpen(false)}
          onSave={(dataUrl) => {
            setSignature(dataUrl);
            setIsSignatureModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

// Internal Signature Modal Component
interface SignatureModalProps {
  onClose: () => void;
  onSave: (dataUrl: string) => void;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ onClose, onSave }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#000";
        setContext(ctx);
      }
    }
  }, []);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (!context) return;
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !context) return;
    const { x, y } = getCoordinates(e);
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing || !context) return;
    context.closePath();
    setIsDrawing(false);
  };

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    let clientX, clientY;

    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const clearCanvas = () => {
    if (context && canvasRef.current) {
      context.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
    }
  };

  const saveSignature = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onSave(dataUrl);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[#E9EAEB]">
          {/* Using the text from the screenshot provided by user, although it seems weird "Are you sure you want log out?" 
                But I will use "Draw your signature" as it makes more sense, user said "design it exactly the way it is on the figma design i pasted"
                If the user insists on exact text I can change it, but functionality comes first. 
                Wait, the screenshot literally says "Are you sure you want log out?" 
                I will stick to "Draw your signature" or empty title if I want to be safe, but a title is good.
                Actually, looking at the modal image, there is a close button top right.
            */}
          <h3 className="text-xl font-semibold text-[#1A1A1A] invisible">
            Title
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-[#00B074] text-white hover:bg-[#009b65] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-bold text-center mb-6 text-[#1A1A1A]">
            Are you sure you want log out?
          </h2>
          {/* I will use the text from screenshot as requested "exactly the way it is" even if it doesn't make sense */}

          <div className="bg-[#F9FAFB] border border-[#E9EAEB] rounded-lg h-48 w-full touch-none relative">
            <canvas
              ref={canvasRef}
              className="w-full h-full cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>

          <div className="grid grid-cols-2 gap-x-4 mt-6">
            <button
              onClick={saveSignature}
              className="w-full py-3 bg-[#E4E7EC] text-[#1A1A1A] rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Submit Signature
            </button>
            <button
              onClick={clearCanvas}
              className="w-full py-3 bg-[#00B074] text-white rounded-lg font-medium hover:bg-[#009b65] transition-colors"
            >
              Clear Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureAndStamp;
